import { DestroyRef, inject, signal } from '@angular/core';
import { DonationAmountService } from '@app/next/forms/features/donation-amount/donation-amount.service';
import {
  NextFieldKey,
  nextFieldPath,
} from '@app/next/forms/features/next-form-known-fields';
import {
  CampaignField,
  CampaignSingleSelectInput,
  CampaignSingleSelectInputField,
  ISelectInputItem,
  IntervalBase,
  PaymentInterval,
  isCampaignSelectField,
  recurringIntervals,
} from '@fmnts/api/next';
import { Maybe, isString } from '@fmnts/core';
import { getUnique } from '@fmnts/core/collection';
import * as Decimal from '@fmnts/core/decimal';
import {
  distinctUntilArrayItemChanged,
  distinctUntilFormKeysChanged,
} from '@fmnts/core/rxjs';
import {
  AbstractDeclarativeInputFieldComponent,
  ConditionalService,
} from '@fmnts/declarative-forms';
import {
  filterSelectItems,
  toItemsWithCondition,
} from '@fmnts/declarative-forms/common';
import { FormFieldService } from '@formunauts/shared/campaigning/data-access';
import * as Eq from 'effect/Equivalence';
import * as O from 'effect/Option';
import {
  EMPTY,
  Observable,
  OperatorFunction,
  combineLatest,
  defer,
  filter,
  map,
  of,
  pairwise,
  shareReplay,
  startWith,
} from 'rxjs';

export type AmountProposalItem = ISelectInputItem & { value: Decimal.Numeric };
export type AmountFieldValue = Decimal.Decimal | null;
export const AmountFieldValueEquivalence: Eq.Equivalence<AmountFieldValue> =
  Eq.mapInput(O.getEquivalence(Decimal.Equivalence), O.fromNullable);
export const AmountFieldValueFromUnknown = (u: unknown): AmountFieldValue =>
  O.getOrNull(Decimal.fromUnknown(u));

type CurrencyItem = Record<`${CurrencyStyle}`, string>;

export enum CurrencyStyle {
  Symbol = 'symbol',
  Code = 'value',
}

export type CampaignAmountField = CampaignField<
  'input',
  CampaignSingleSelectInput
>;

/**
 * Base implementation for an amount field.
 */
export abstract class AbstractAmountFieldComponent extends AbstractDeclarativeInputFieldComponent<
  CampaignAmountField,
  AmountFieldValue
> {
  protected readonly conditionalService = inject(ConditionalService);
  protected readonly fieldHelper = inject(FormFieldService);
  protected readonly donationAmountService = inject(DonationAmountService);
  protected readonly destroyRef = inject(DestroyRef);

  protected readonly _value = signal<AmountFieldValue>(null);

  /**
   * Emits with the available amount proposals
   */
  protected readonly items$ = defer(() => this.getItems$()).pipe(
    shareReplay(1),
  );

  /**
   * Emits the current currency item from the currency field.
   */
  private readonly _currencyItem$ = defer(() => this.getCurrencyItem$()).pipe(
    shareReplay(1),
  );

  /** Emits currency using the currency code (e.g. 'EUR'). */
  protected readonly currencyCode$ = this._currencyItem$.pipe(
    displayCurrencyAs(CurrencyStyle.Code),
  );
  /** Emits currency using the symbol (e.g. 'EUR'). */
  protected readonly currencySymbol$ = this._currencyItem$.pipe(
    displayCurrencyAs(CurrencyStyle.Symbol),
  );

  /**
   * Get the calculation base from the "donation_amount_per_interval" field
   * to correctly interpret values in case that "show_per_interval" is active
   *
   * @param amountPerIntervalFieldPath Path to the amount per interval field
   * @returns
   */
  protected getCalculationBase(
    amountPerIntervalFieldPath = nextFieldPath.donationAmountPerInterval,
  ): IntervalBase {
    const amountPerIntervalField = this.declaration.get(
      amountPerIntervalFieldPath,
    );

    if (!amountPerIntervalField) {
      throw new Error(
        `Field with key ${amountPerIntervalFieldPath.join('.')} expected, but not available.`,
      );
    }

    return amountPerIntervalField.attrs?.['calculation_base'];
  }

  /**
   * @returns
   * `true` if the amount should be shown based on the selected interval.
   */
  protected getShowPerInterval(field = this.field): boolean {
    // Must be explicitly set to true.
    return field.attrs?.show_per_interval === true;
  }

  get empty(): boolean {
    return !this._value();
  }

  get showError(): boolean {
    return (!this.empty || this.control.touched) && this.control.invalid;
  }

  /**
   * @param field
   * @param form
   *
   * @returns
   * An `Observable` that emits with the filtered items
   */
  private getItems$(
    field = this.field,
    form = this.form,
  ): Observable<AmountProposalItem[]> {
    if (!field) {
      // No items without a field, nothing to emit
      return EMPTY;
    }

    const itemsWithConditions = this._prepareItems(field);
    // First extract all relevant field keys
    const observableKeys = getUnique(
      itemsWithConditions.flatMap(({ condition }) =>
        condition
          ? this.conditionalService.extractReferencedKeys(condition)
          : [],
      ),
    );

    if (observableKeys.length === 0) {
      // When there are no conditions, the items won't change
      // Also fallback to an empty array, in case no
      // amount proposals are available
      return of(field.widgetOptions.items ?? []);
    }

    // Observable that emits on relevant form changes
    const changes$ = form.flatValueChanges.pipe(
      distinctUntilFormKeysChanged(observableKeys),
      startWith(form.flatValue),
    );

    // Observable that emits items with conditions
    const itemsWithConditions$ = of(itemsWithConditions);

    return itemsWithConditions$.pipe(
      filterSelectItems(
        () => changes$,
        (changes, { condition }) =>
          condition
            ? (this.conditionalService.checkObj(changes, condition) ?? true)
            : true,
      ),
      map((filteredItems) => filteredItems.map((i) => i.item)),
      distinctUntilArrayItemChanged((prev, cur) => prev.value === cur.value),
      shareReplay(1),
    );
  }

  /**
   * @param field The field
   *
   * @returns
   * Array of items with conditions.
   */
  private _prepareItems(field: CampaignSingleSelectInputField) {
    const {
      widgetOptions: { items: originalItems = [], itemConditions = {} },
    } = field;

    const items = toItemsWithCondition(originalItems, itemConditions);

    // In case that "show_per_interval" isn't active, we can treat the items
    // as they are and return them with conditions
    if (!this.getShowPerInterval(field)) {
      return items;
    }

    const base = this.getCalculationBase();
    if (!base) {
      throw new Error(
        `Calculation base must be given when 'show_per_interval' is true.`,
      );
    }

    // If "show_per_interval" is active, we need to adapt
    // the available items
    const intervalFieldKey = NextFieldKey.Interval;
    const oneOffCondition = {
      key: intervalFieldKey,
      equals: PaymentInterval.OneOff,
    };

    return items.flatMap((item) => {
      // We don't interfer with items that already have their own
      // condition
      if (item.condition) {
        return [item];
      }

      const itemValue = new Decimal.Decimal(item.item.value);
      return [
        // Prepare an item for each recurring interval and
        // * set the condition on that interval
        // * transform the value to that interval
        ...recurringIntervals.map((interval) => {
          const transformedValue = this.donationAmountService
            .toInterval(itemValue, base, interval)
            .toDecimalPlaces(2)
            .toString();

          return {
            item: {
              value: transformedValue,
              label: transformedValue,
            },
            condition: {
              key: intervalFieldKey,
              equals: interval,
            },
          };
        }),
        // Also append the untransformed value as "one-off"
        {
          item: item.item,
          condition: oneOffCondition,
        },
      ];
    });
  }

  /**
   * @param currencyFieldKey Path to the currency field
   *
   * @returns
   * Observable that emits the selected item in the currency select
   */
  private getCurrencyItem$(
    currencyFieldKey = nextFieldPath.currency,
  ): Observable<Maybe<CurrencyItem>> {
    // Get field and control
    const field = this.declaration.get<CampaignField>(currencyFieldKey);
    const control = this.getControl(currencyFieldKey);

    // Get as observables
    const field$ = of(field).pipe(filter(isCampaignSelectField));
    const selectedCurrency$ = control.valueChanges.pipe(
      startWith(control.value),
      map<unknown, Maybe<string>>((selectedCurrency) =>
        !selectedCurrency ? undefined : selectedCurrency.toString(),
      ),
    );

    // Observable that emits the selected item in the currency select
    return combineLatest([selectedCurrency$, field$]).pipe(
      map(([selectedCurrency, currencyField]) =>
        !selectedCurrency
          ? undefined
          : (this.fieldHelper.findItemByPredicate(
              currencyField,
              (value) => value.toString() === selectedCurrency,
            ) as unknown as Maybe<CurrencyItem>),
      ),
    );
  }

  protected getInterval$() {
    const intervalCtrl = this.getControl(nextFieldPath.directDebitInterval);
    return intervalCtrl.valueChanges.pipe(
      startWith(intervalCtrl.value),
      pairwise<PaymentInterval>(),
      filter(
        // Only operate on changes from one recurring interval
        // to another, as these are the only items that make
        // sense to transform
        ([from, to]) =>
          from !== to &&
          recurringIntervals.includes(from) &&
          recurringIntervals.includes(to),
      ),
    );
  }

  private getControl(fieldPath: [string, string]) {
    const control = this.form.get(fieldPath);

    if (!control) {
      throw new Error(
        `Field with key ${fieldPath.join('.')} expected, but not available.`,
      );
    }

    return control;
  }
}

/**
 * @returns
 * Operator to display the currency either using a symbol representation
 * or the currency code
 */
export function displayCurrencyAs(
  currencyStyle: CurrencyStyle,
): OperatorFunction<Maybe<CurrencyItem>, string> {
  return map((value) => {
    const currency = value?.[currencyStyle];
    return isString(currency) ? currency : '';
  });
}
